import React from 'react';
import PropTypes from 'prop-types';

/**
 * SettingsButton Component
 * 
 * Pre-defined button types for settings/configuration actions:
 * - theme: Customize theme colors
 * - logo: Customize logo
 * - floorplan: Customize floorplan
 * - export: Export configuration
 * - import: Import configuration
 * 
 * On mobile: displays as a chip (icon only, rounded-full)
 * On desktop: displays as a full button (icon + text, rounded-lg)
 */
const SettingsButton = ({ type, onClick }) => {
  const baseClasses = 'p-2 sm:px-4 sm:py-2 rounded-full sm:rounded-lg text-sm font-medium transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center justify-center sm:gap-2';

  const configs = {
    theme: {
      style: {
        backgroundColor: 'var(--color-primary)',
        color: 'var(--color-surface)',
        borderColor: 'var(--color-primary)',
        borderWidth: '1px',
        borderStyle: 'solid',
      },
      focusRingColor: 'var(--color-primary)',
      icon: (
        <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 2a2 2 0 00-2 2v11a3 3 0 106 0V4a2 2 0 00-2-2H4zm1 14a1 1 0 100-2 1 1 0 000 2zm5-1.757l4.9-4.9a2 2 0 000-2.828L13.485 5.1a2 2 0 00-2.828 0L10 5.757v8.486zM16 18H9.071l6-6H16a2 2 0 012 2v2a2 2 0 01-2 2z" clipRule="evenodd" />
        </svg>
      ),
      text: 'Customize Theme',
    },
    logo: {
      style: {
        backgroundColor: 'var(--color-primary)',
        color: 'var(--color-surface)',
        borderColor: 'var(--color-primary)',
        borderWidth: '1px',
        borderStyle: 'solid',
      },
      focusRingColor: 'var(--color-primary)',
      icon: (
        <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
        </svg>
      ),
      text: 'Customize Logo',
    },
    floorplan: {
      style: {
        backgroundColor: 'var(--color-primary)',
        color: 'var(--color-surface)',
        borderColor: 'var(--color-primary)',
        borderWidth: '1px',
        borderStyle: 'solid',
      },
      focusRingColor: 'var(--color-primary)',
      icon: (
        <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
        </svg>
      ),
      text: 'Tab Settings',
    },
    export: {
      style: {
        backgroundColor: 'var(--color-primary)',
        color: 'var(--color-surface)',
        borderColor: 'var(--color-primary)',
        borderWidth: '1px',
        borderStyle: 'solid',
      },
      focusRingColor: 'var(--color-primary)',
      icon: (
        <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      ),
      text: 'Export Config',
    },
    import: {
      style: {
        backgroundColor: 'var(--color-primary)',
        color: 'var(--color-surface)',
        borderColor: 'var(--color-primary)',
        borderWidth: '1px',
        borderStyle: 'solid',
      },
      focusRingColor: 'var(--color-primary)',
      icon: (
        <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
      ),
      text: 'Import Config',
    },
  };

  const config = configs[type];

  return (
    <button
      type="button"
      onClick={onClick}
      className={baseClasses}
      style={{
        ...config.style,
        '--tw-ring-color': config.focusRingColor,
      }}
      aria-label={config.text}
      title={config.text}
    >
      {config.icon}
      <span className="hidden sm:inline">{config.text}</span>
    </button>
  );
};

SettingsButton.propTypes = {
  type: PropTypes.oneOf(['theme', 'logo', 'floorplan', 'export', 'import']).isRequired,
  onClick: PropTypes.func.isRequired,
};

export default SettingsButton;
